import { api } from "../../lib/api";

export type MovementConcept = {
  id: number;
  type: "income" | "expense";
  name: string;
  color: string | null;
  active: boolean;
  createdAt: string;
};

export type Movement = {
  id: number;
  type: "income" | "expense";
  amountCents: number;
  conceptId: number | null;
  freeformConcept: string | null;
  paymentMethodId: number | null;
  operatorId: number;
  note: string | null;
  occurredAt: string;
  createdAt: string;
  concept: { id: number; type: "income" | "expense"; name: string; color: string | null } | null;
  paymentMethod: { id: number; name: string; color: string } | null;
  status?: "active" | "voided";
  voidedAt?: string | null;
  voidedById?: number | null;
  voidReason?: string | null;
  supersedesMovementId?: number | null;
  reversedByMovementId?: number | null;
  reversalOfMovementId?: number | null;
};

export type MovementAuditEvent = {
  id: number;
  action: "created" | "metadata_updated" | "corrected" | "voided" | "reversed" | string;
  operatorId: number;
  reason: string | null;
  createdAt: string;
  beforeSnapshot: Record<string, unknown> | null;
  afterSnapshot: Record<string, unknown> | null;
};

export type MovementDetail = Movement & {
  auditEvents?: MovementAuditEvent[];
};

export type CorrectMovementInput = {
  type: "income" | "expense";
  amountCents: number;
  conceptId?: number | null;
  freeformConcept?: string | null;
  paymentMethodId?: number | null;
  note?: string | null;
  occurredAt?: string;
  reason: string;
  moneyConfirmationToken?: string;
};

export type RecentConcept = MovementConcept & {
  lastUsedAt: string | null;
  usageCount: number;
};

export const movementConceptsApi = {
  list: (params: { type?: "income" | "expense"; includeInactive?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.type) q.set("type", params.type);
    if (params.includeInactive) q.set("includeInactive", "true");
    const qs = q.toString();
    return api<MovementConcept[]>(`/api/movement-concepts${qs ? `?${qs}` : ""}`);
  },
  create: (body: { type: "income" | "expense"; name: string }) =>
    api<MovementConcept>("/api/movement-concepts", { method: "POST", body }),
};

export const movementsApi = {
  create: (body: {
    type: "income" | "expense";
    amountCents: number;
    conceptId?: number | null;
    freeformConcept?: string | null;
    paymentMethodId?: number | null;
    note?: string | null;
    moneyConfirmationToken?: string;
  }) => api<Movement>("/api/movements", { method: "POST", body }),
  get: (id: number) => api<MovementDetail>(`/api/movements/${id}`),
  updateNote: (id: number, body: { note: string | null }) =>
    api<MovementDetail>(`/api/movements/${id}/note`, { method: "PATCH", body }),
  void: (id: number, body: { reason: string }) =>
    api<MovementDetail>(`/api/movements/${id}/void`, { method: "POST", body }),
  correct: (id: number, body: CorrectMovementInput) =>
    api<MovementDetail>(`/api/movements/${id}/correct`, { method: "POST", body }),
  reverse: (id: number, body: { reason: string; moneyConfirmationToken?: string }) =>
    api<MovementDetail>(`/api/movements/${id}/reverse`, { method: "POST", body }),
  list: (params: {
    type?: "income" | "expense";
    from?: string;
    to?: string;
    operatorId?: number;
    conceptId?: number;
    limit?: number;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.type) q.set("type", params.type);
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    if (params.operatorId) q.set("operatorId", String(params.operatorId));
    if (params.conceptId) q.set("conceptId", String(params.conceptId));
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return api<Movement[]>(`/api/movements${qs ? `?${qs}` : ""}`);
  },
  recentConcepts: (params: { type?: "income" | "expense"; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.type) q.set("type", params.type);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return api<RecentConcept[]>(`/api/movements/recent-concepts${qs ? `?${qs}` : ""}`);
  },
};
